import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "../../../contexts/AuthContext";

const UpdateConfigsForm = ({
  availableLogins = [],
  availableRiskFields = [],
  availablePipFields = [],
}) => {
  const [selectedLogins, setSelectedLogins] = useState([]);
  const [manualLogin, setManualLogin] = useState("");
  const [isActiveChange, setIsActiveChange] = useState(null);
  const [riskConfigChanges, setRiskConfigChanges] = useState({});
  const [pipConfigChanges, setPipConfigChanges] = useState({});
  const [updateLoading, setUpdateLoading] = useState(false);
  const [message, setMessage] = useState("");
  const { apiBaseUrl } = useAuth();

  // فیلدهای مهم برای نمایش اولویت‌دار
  const priorityRiskFields = [
    "max_overall_drawdown_percentage",
    "max_daily_drawdown_percentage",
    "max_position_drawdown_percentage",
    "global_max_overall_drawdown_percentage",
    "global_max_daily_drawdown_percentage",
    "enable_overall_drawdown_monitoring",
    "enable_daily_drawdown_monitoring",
    "enable_position_drawdown_monitoring",
    "close_position_on_max_drawdown_breach",
  ];

  const priorityPipFields = [
    "enable_pip_based_sl",
    "enable_pip_based_tp",
    "default_sl_distance_pips",
    "default_tp_distance_pips",
  ];

  const resetForm = () => {
    setSelectedLogins([]);
    setManualLogin("");
    setIsActiveChange(null);
    setRiskConfigChanges({});
    setPipConfigChanges({});
    setMessage("");
  };

  const addManualLogin = () => {
    const loginValue = manualLogin.trim();
    if (loginValue && !selectedLogins.includes(loginValue)) {
      setSelectedLogins([...selectedLogins, loginValue]);
      setManualLogin("");
    }
  };

  const toggleLoginSelection = (login) => {
    if (selectedLogins.includes(login)) {
      setSelectedLogins(selectedLogins.filter((l) => l !== login));
    } else {
      setSelectedLogins([...selectedLogins, login]);
    }
  };

  const removeLogin = (login) => {
    setSelectedLogins(selectedLogins.filter((l) => l !== login));
  };

  const handleUpdateConfigs = async () => {
    if (selectedLogins.length === 0) {
      setMessage("لطفاً حداقل یک Login وارد کنید");
      return;
    }

    if (
      isActiveChange === null &&
      Object.keys(riskConfigChanges).length === 0 &&
      Object.keys(pipConfigChanges).length === 0
    ) {
      setMessage("لطفاً حداقل یک تغییر وارد کنید");
      return;
    }

    setUpdateLoading(true);
    setMessage("");

    try {
      const payload = {
        type: "custom_logins",
        logins: selectedLogins,
      };

      if (isActiveChange !== null) {
        payload.is_active = isActiveChange;
      }

      if (Object.keys(riskConfigChanges).length > 0) {
        payload.risk_config = riskConfigChanges;
      }

      if (Object.keys(pipConfigChanges).length > 0) {
        payload.pip_config = pipConfigChanges;
      }

      const response = await axios.post(
        `${apiBaseUrl}/api/admin/final/accounts/update/configs`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (response.data.ok || response.data.success) {
        setMessage("تنظیمات با موفقیت به‌روزرسانی شد");
        resetForm();
      }
    } catch (error) {
      console.error("Error:", error);
      setMessage(error.response?.data?.message || "خطا در به‌روزرسانی تنظیمات");
    } finally {
      setUpdateLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-white mb-6">
        به‌روزرسانی تنظیمات حساب‌ها
      </h3>

      {message && (
        <div
          className={`p-4 rounded-lg text-center ${
            message.includes("موفقیت")
              ? "bg-green-900 border border-green-600 text-green-400"
              : "bg-red-900 border border-red-600 text-red-400"
          }`}
        >
          {message}
        </div>
      )}

      {/* انتخاب Login ها */}
      <div className="bg-gray-800 border border-gray-600 rounded-lg p-4 space-y-4">
        <h4 className="text-white font-medium">انتخاب حساب‌ها برای ویرایش</h4>

        {/* افزودن Login دستی */}
        <div className="bg-gray-700 rounded-lg p-4">
          <label className="block text-white text-sm font-medium mb-2">
            افزودن Login به صورت دستی:
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={manualLogin}
              onChange={(e) => setManualLogin(e.target.value)}
              placeholder="شماره Login را وارد کنید..."
              className="flex-1 p-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-green-500 focus:outline-none"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addManualLogin();
                }
              }}
            />
            <button
              type="button"
              onClick={addManualLogin}
              disabled={!manualLogin.trim()}
              className="px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-lg transition duration-200 whitespace-nowrap"
            >
              افزودن
            </button>
          </div>
        </div>

        {/* انتخاب از لیست موجود */}
        {availableLogins.length > 0 && (
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              یا از لیست حساب‌های موجود انتخاب کنید:
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-60 overflow-y-auto bg-gray-900 p-3 rounded-lg">
              {availableLogins.map((loginObj) => (
                <label
                  key={loginObj.login}
                  className="flex items-center gap-3 bg-gray-700 p-3 rounded-lg cursor-pointer hover:bg-gray-600 transition"
                >
                  <input
                    type="checkbox"
                    checked={selectedLogins.includes(loginObj.login)}
                    onChange={() => toggleLoginSelection(loginObj.login)}
                    className="w-5 h-5 accent-green-600"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-medium truncate">
                      {loginObj.login}
                    </div>
                    {loginObj.name && (
                      <div className="text-gray-400 text-xs truncate">
                        {loginObj.name}
                      </div>
                    )}
                    <div
                      className={`text-xs ${
                        loginObj.isActive ? "text-green-400" : "text-red-400"
                      }`}
                    >
                      {loginObj.isActive ? "فعال" : "غیرفعال"}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* نمایش Login های انتخاب شده */}
        {selectedLogins.length > 0 && (
          <div className="bg-gray-900 rounded-lg p-3">
            <p className="text-gray-300 text-sm mb-2">
              Login های انتخاب شده ({selectedLogins.length} مورد):
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedLogins.map((login) => (
                <div
                  key={login}
                  className="bg-green-600 text-white px-3 py-1 rounded-full text-sm flex items-center gap-2"
                >
                  {login}
                  <button
                    type="button"
                    onClick={() => removeLogin(login)}
                    className="hover:bg-green-700 rounded-full p-1 transition duration-200"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* فرم ویرایش */}
      <div className="bg-gray-800 border border-yellow-600 rounded-lg p-6 space-y-6">
        <h4 className="text-lg font-semibold text-white border-b border-gray-600 pb-3">
          تغییرات مورد نظر
        </h4>

        {/* تغییر وضعیت */}
        <div>
          <label className="block text-white font-medium mb-2">
            تغییر وضعیت (اختیاری):
          </label>
          <select
            value={isActiveChange || ""}
            onChange={(e) => setIsActiveChange(e.target.value || null)}
            className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-green-500 focus:outline-none"
          >
            <option value="">بدون تغییر</option>
            <option value="true">فعال</option>
            <option value="false">غیرفعال</option>
          </select>
        </div>

        {/* Risk Config */}
        <div>
          <label className="block text-white font-medium mb-2">
            تغییرات Risk Config:
          </label>
          <FieldEditor
            availableFields={availableRiskFields}
            priorityFields={priorityRiskFields}
            changes={riskConfigChanges}
            onChange={setRiskConfigChanges}
            allowCustomFields={true}
          />
        </div>

        {/* Pip Config */}
        <div>
          <label className="block text-white font-medium mb-2">
            تغییرات Pip Config:
          </label>
          <FieldEditor
            availableFields={availablePipFields}
            priorityFields={priorityPipFields}
            changes={pipConfigChanges}
            onChange={setPipConfigChanges}
            allowCustomFields={true}
          />
        </div>

        {/* دکمه‌ها */}
        <div className="flex gap-3 pt-4 border-t border-gray-600">
          <button
            onClick={resetForm}
            className="flex-1 py-3 px-6 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition duration-200"
          >
            پاک کردن فرم
          </button>
          <button
            onClick={handleUpdateConfigs}
            disabled={updateLoading || selectedLogins.length === 0}
            className="flex-1 py-3 px-6 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white font-medium rounded-lg transition duration-200 flex items-center justify-center gap-2"
          >
            {updateLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                در حال ذخیره...
              </>
            ) : (
              "ذخیره تغییرات"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// کامپوننت ویرایشگر فیلد
const FieldEditor = ({
  availableFields,
  priorityFields = [],
  changes,
  onChange,
  allowCustomFields = false,
}) => {
  const [selectedField, setSelectedField] = useState("");
  const [customField, setCustomField] = useState("");
  const [fieldValue, setFieldValue] = useState("");
  const [useCustomField, setUseCustomField] = useState(false);

  const handleAdd = () => {
    const field = useCustomField ? customField.trim() : selectedField;
    if (field && fieldValue.trim()) {
      onChange({
        ...changes,
        [field]: fieldValue.trim(),
      });
      setSelectedField("");
      setCustomField("");
      setFieldValue("");
      setUseCustomField(false);
    }
  };

  const handleRemove = (field) => {
    const updated = { ...changes };
    delete updated[field];
    onChange(updated);
  };

  const handleChange = (field, value) => {
    onChange({
      ...changes,
      [field]: value,
    });
  };

  // Sort fields: priority fields first, then alphabetically
  const sortedFields = [...availableFields].sort((a, b) => {
    const aPriority = priorityFields.includes(a);
    const bPriority = priorityFields.includes(b);
    if (aPriority && !bPriority) return -1;
    if (!aPriority && bPriority) return 1;
    return a.localeCompare(b);
  });

  return (
    <div className="space-y-3">
      {allowCustomFields && (
        <div className="flex items-center gap-3 bg-gray-700 p-3 rounded-lg">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={useCustomField}
              onChange={(e) => {
                setUseCustomField(e.target.checked);
                setSelectedField("");
                setCustomField("");
              }}
              className="w-4 h-4 accent-green-600"
            />
            <span className="text-white text-sm">استفاده از فیلد دلخواه</span>
          </label>
        </div>
      )}

      <div className="flex gap-2">
        {useCustomField ? (
          <input
            type="text"
            value={customField}
            onChange={(e) => setCustomField(e.target.value)}
            placeholder="نام فیلد را وارد کنید..."
            className="flex-1 p-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-green-500 focus:outline-none text-sm"
          />
        ) : (
          <select
            value={selectedField}
            onChange={(e) => setSelectedField(e.target.value)}
            className="flex-1 p-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-green-500 focus:outline-none text-sm"
          >
            <option value="">انتخاب فیلد...</option>
            {sortedFields
              .filter((field) => !changes[field])
              .map((field) => {
                const isPriority = priorityFields.includes(field);
                return (
                  <option
                    key={field}
                    value={field}
                    className={isPriority ? "bg-yellow-900 font-bold" : ""}
                  >
                    {isPriority ? "⭐ " : ""}
                    {field}
                  </option>
                );
              })}
          </select>
        )}
        <input
          type="text"
          value={fieldValue}
          onChange={(e) => setFieldValue(e.target.value)}
          placeholder="مقدار..."
          className="w-40 p-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-green-500 focus:outline-none text-sm"
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
        <button
          onClick={handleAdd}
          disabled={
            (!useCustomField && !selectedField) ||
            (useCustomField && !customField.trim()) ||
            !fieldValue.trim()
          }
          className="px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-lg transition duration-200 text-sm whitespace-nowrap"
        >
          افزودن
        </button>
      </div>

      {Object.keys(changes).length > 0 ? (
        <div className="bg-gray-900 rounded-lg p-3 space-y-2 max-h-96 overflow-y-auto">
          {Object.entries(changes).map(([field, value]) => {
            const isPriority = priorityFields.includes(field);
            return (
              <div
                key={field}
                className={`flex items-center gap-2 bg-gray-800 rounded p-2 border-l-4 ${
                  isPriority ? "border-yellow-500" : "border-gray-600"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-gray-400 text-xs">{field}</span>
                    {isPriority && (
                      <span className="text-xs px-2 py-0.5 rounded bg-yellow-900/50 text-yellow-300">
                        مهم
                      </span>
                    )}
                  </div>
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => handleChange(field, e.target.value)}
                    className="w-full p-2 bg-gray-900 border border-gray-600 rounded text-white text-sm focus:border-green-500 focus:outline-none"
                  />
                </div>
                <button
                  onClick={() => handleRemove(field)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0"
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-gray-500 text-sm text-center py-4">
          هنوز فیلدی اضافه نشده است
        </p>
      )}
    </div>
  );
};

export default UpdateConfigsForm;
